import React from 'react'
import { Layout, Typography, Button, Row, Col, Card } from 'antd'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { siteSettingsApi } from '../../services/api'
import { ShoppingCartOutlined, GlobalOutlined, SafetyCertificateOutlined, TruckOutlined } from '@ant-design/icons'

const { Content } = Layout
const { Title, Paragraph } = Typography

const OnlineShopPage: React.FC = () => {
  const { i18n } = useTranslation()
  const currentLang = i18n.language as 'en' | 'zh'

  // Fetch site settings for dynamic content
  const { data: siteSettingsData } = useQuery({
    queryKey: ['publicSiteSettings'],
    queryFn: siteSettingsApi.getPublicSettings,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  const settings = siteSettingsData?.settings

  // Helper function to get localized text
  const getLocalizedText = (text: { en: string; zh: string } | undefined, fallback: string = '') => {
    if (!text) return fallback
    return text[currentLang] || text.en || fallback
  }

  return (
    <>
      <Helmet>
        <title>Online Shop - {getLocalizedText(settings?.siteName, 'MedCareHub')}</title>
        <meta name="description" content={`Shop authentic ${getLocalizedText(settings?.siteName, 'healthcare')} products online with secure payment, fast shipping, and warranty protection.`} />
      </Helmet>

      <Content>
        {/* Hero Section */}
        <section className="py-16 bg-gradient-to-r from-green-600 to-green-800 text-white">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <Title level={1} className="text-4xl font-bold text-white mb-4">
              {getLocalizedText(settings?.siteName, 'Healthcare')} Online Shop
            </Title>
            <Paragraph className="text-xl text-green-100 max-w-3xl mx-auto mb-8">
              Shop authentic {getLocalizedText(settings?.siteName, 'healthcare')} products with secure payment, fast shipping, and comprehensive warranty protection.
            </Paragraph>
            <Button 
              type="primary" 
              size="large" 
              icon={<ShoppingCartOutlined />}
              className="bg-white text-green-600 border-white hover:bg-gray-100"
            >
              Shop Now
            </Button>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <Title level={2} className="text-3xl font-bold text-gray-900 mb-4">
                Why Shop with {getLocalizedText(settings?.siteName, 'Us')}
              </Title>
              <Paragraph className="text-lg text-gray-600">
                Enjoy the benefits of shopping directly from the manufacturer
              </Paragraph>
            </div>
            <Row gutter={[24, 24]}>
              <Col xs={24} md={6}>
                <Card className="h-full text-center border-0 shadow-lg">
                  <SafetyCertificateOutlined className="text-4xl text-green-600 mb-4" />
                  <Title level={4} className="text-gray-900 mb-3">Authentic Products</Title>
                  <Paragraph className="text-gray-600">
                    100% genuine {getLocalizedText(settings?.siteName, 'healthcare')} products with full manufacturer warranty
                  </Paragraph>
                </Card>
              </Col>
              <Col xs={24} md={6}>
                <Card className="h-full text-center border-0 shadow-lg">
                  <TruckOutlined className="text-4xl text-green-600 mb-4" />
                  <Title level={4} className="text-gray-900 mb-3">Fast Shipping</Title>
                  <Paragraph className="text-gray-600">
                    Free shipping on orders over $100 with tracking information
                  </Paragraph>
                </Card>
              </Col>
              <Col xs={24} md={6}>
                <Card className="h-full text-center border-0 shadow-lg">
                  <GlobalOutlined className="text-4xl text-green-600 mb-4" />
                  <Title level={4} className="text-gray-900 mb-3">Global Delivery</Title>
                  <Paragraph className="text-gray-600">
                    Worldwide shipping to over 50 countries and regions
                  </Paragraph>
                </Card>
              </Col>
              <Col xs={24} md={6}>
                <Card className="h-full text-center border-0 shadow-lg">
                  <ShoppingCartOutlined className="text-4xl text-green-600 mb-4" />
                  <Title level={4} className="text-gray-900 mb-3">Secure Checkout</Title>
                  <Paragraph className="text-gray-600">
                    SSL-encrypted transactions with multiple payment options
                  </Paragraph>
                </Card>
              </Col>
            </Row>
          </div>
        </section>

        {/* Product Categories */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <Title level={2} className="text-3xl font-bold text-gray-900 mb-4">
                Shop by Category
              </Title>
              <Paragraph className="text-lg text-gray-600">
                Find the perfect healthcare solution for your needs
              </Paragraph>
            </div>
            <Row gutter={[24, 24]}>
              <Col xs={24} md={8}>
                <Card 
                  hoverable
                  cover={
                    <div className="h-48 bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                      <span className="text-blue-600 text-lg font-semibold">Blood Pressure Monitors</span>
                    </div>
                  }
                >
                  <Card.Meta
                    title="Blood Pressure Monitors"
                    description="Accurate and reliable blood pressure monitoring for home use"
                  />
                  <Button type="link" className="p-0 mt-3">
                    <Link to="/products?category=upper-arm-blood-pressure-monitor">Shop Now →</Link>
                  </Button>
                </Card>
              </Col>
              <Col xs={24} md={8}>
                <Card 
                  hoverable
                  cover={
                    <div className="h-48 bg-gradient-to-br from-green-100 to-green-200 flex items-center justify-center">
                      <span className="text-green-600 text-lg font-semibold">Body Composition</span>
                    </div>
                  }
                >
                  <Card.Meta
                    title="Body Composition Monitors"
                    description="Complete body analysis including weight, BMI, and body fat"
                  />
                  <Button type="link" className="p-0 mt-3">
                    <Link to="/products?category=body-composition-monitor">Shop Now →</Link>
                  </Button>
                </Card>
              </Col>
              <Col xs={24} md={8}>
                <Card 
                  hoverable
                  cover={
                    <div className="h-48 bg-gradient-to-br from-purple-100 to-purple-200 flex items-center justify-center">
                      <span className="text-purple-600 text-lg font-semibold">Respiratory Care</span>
                    </div>
                  }
                >
                  <Card.Meta
                    title="Respiratory Care"
                    description="Nebulizers and respiratory health monitoring devices"
                  />
                  <Button type="link" className="p-0 mt-3">Shop Now →</Button>
                </Card>
              </Col>
            </Row>
          </div>
        </section>

        {/* Customer Support */}
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <Title level={2} className="text-3xl font-bold text-gray-900 mb-4">
              Need Help Shopping?
            </Title>
            <Paragraph className="text-lg text-gray-600 mb-8">
              Our customer service team is here to help you find the right product
            </Paragraph>
            <Row gutter={[24, 24]} justify="center">
              <Col xs={24} md={8}>
                <Card className="text-center">
                  <Title level={4} className="text-blue-600 mb-2">Live Chat</Title>
                  <Paragraph className="text-gray-600 mb-4">
                    Chat with our experts Monday-Friday 9AM-6PM
                  </Paragraph>
                  <Button type="primary">Start Chat</Button>
                </Card>
              </Col>
              <Col xs={24} md={8}>
                <Card className="text-center">
                  <Title level={4} className="text-blue-600 mb-2">Phone Support</Title>
                  <Paragraph className="text-gray-600 mb-4">
                    Call us at 1-800-OMRON-10 for immediate assistance
                  </Paragraph>
                  <Button type="default">Call Now</Button>
                </Card>
              </Col>
            </Row>
          </div>
        </section>
      </Content>
    </>
  )
}

export default OnlineShopPage